import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHolidayNotices, HolidayNotice } from "@/hooks/useHolidayNotices";
import { isoToZoneParts } from "@/components/psychologist/calendar/timeZoneHelpers";
import {
  HolidaySessionsDialog,
  HolidaySessionsInfo,
} from "@/components/psychologist/calendar/HolidaySessionsDialog";
import { sessionsOnHoliday } from "@/pages/psychologist/tabs/comunidad/holidaySessions";
import { formatHolidayDay } from "@/utils/holidays";
import { calendarPath } from "@/routes/slugs";
import { useToast } from "@/hooks/useToast";
import { activityTracker } from "@/utils/activityTracker";

/**
 * Aviso de feriados arriba de la Agenda (la primera pantalla al entrar, en la web y en la app):
 * mismo estilo que el aviso de Comunidad. Un solo aviso con una línea por feriado de los
 * próximos 14 días, con su estado (ver `useHolidayNotices`). "Bloquear mi agenda" usa el mismo
 * camino que Comunidad (panel de bloqueo con el feriado cargado; si hay sesiones, el cartel de
 * sesiones). "Voy a trabajar" lo saca de la Agenda (queda guardado; sigue viéndose en Comunidad).
 */
/** `className`: margen exterior según dónde se monta (no deja espacio vacío si no hay aviso). */
export const HolidayAgendaNotice = ({
  className = "",
}: {
  className?: string;
}) => {
  const navigate = useNavigate();
  const { showWarning } = useToast();
  const { notices, timezone, countryName, marcarTrabajo } = useHolidayNotices();
  const [checking, setChecking] = useState<string | null>(null);
  const [sessionsInfo, setSessionsInfo] = useState<HolidaySessionsInfo | null>(
    null,
  );

  // "Voy a trabajar" ya decidió: ese feriado no se muestra más en la Agenda (sigue en Comunidad).
  const visibles = notices.filter((n) => !n.trabaja);

  const bloquear = async (n: HolidayNotice) => {
    if (checking) return;
    // Métricas de Actividad: tocó "Bloquear mi agenda" en el aviso de feriados.
    activityTracker.trackAction("feriado_bloquear");
    setChecking(n.date);
    try {
      // Igual que en Comunidad: si en el medio aparecieron sesiones, no se deja bloquear.
      const sessions = await sessionsOnHoliday(n.date, timezone);
      if (sessions.length > 0) {
        setSessionsInfo({ date: n.date, name: n.name, sessions });
        activityTracker.trackAction("feriado_sesiones_vistas");
        return;
      }
      navigate(
        `/calendar?${new URLSearchParams({ config: "block", date: n.date, reason: `Feriado: ${n.name}` })}`,
      );
    } catch {
      showWarning(
        "No pudimos revisar tu agenda de ese día. Probá de nuevo en un rato.",
      );
    } finally {
      setChecking(null);
    }
  };

  const dialog = (
    <HolidaySessionsDialog
      info={sessionsInfo}
      timezone={timezone}
      onClose={() => setSessionsInfo(null)}
    />
  );

  if (visibles.length === 0) return dialog;

  return (
    <div className={className}>
      <div
        data-testid="holiday-notice"
        className="flex flex-col gap-3 rounded-[20px] bg-white px-4 py-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:px-6"
      >
        {visibles.map((n) => (
          <div
            key={n.date}
            className="flex flex-wrap items-center gap-3"
            data-feriado={n.date}
          >
            <AlertTriangle size={24} className="shrink-0 text-amber-500" />
            <div className="flex min-w-0 flex-grow flex-col gap-0.5 md:min-w-[240px]">
              {n.state === 1 ? (
                <>
                  <span className="text-[13.5px] font-bold text-amber-700">
                    Se viene {n.name} en {countryName}, el{" "}
                    {formatHolidayDay(n.date)}
                  </span>
                  <span className="text-xs leading-snug text-gray-500">
                    ¿Vas a trabajar? Si no, bloqueá tu agenda para que no te
                    agenden sesiones ese día.
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[13.5px] font-bold text-amber-700">
                    El {formatHolidayDay(n.date)} es {n.name} en {countryName} y
                    tenés {n.sessions.length}{" "}
                    {n.sessions.length === 1
                      ? "paciente agendado"
                      : "pacientes agendados"}
                  </span>
                  <span className="text-xs leading-snug text-[#4A3D73]">
                    {n.sessions
                      .map(
                        (s) =>
                          `${s.patientName || "Paciente"} ${isoToZoneParts(s.startIso, timezone).time} hs`,
                      )
                      .join(" · ")}
                  </span>
                  <span className="text-xs leading-snug text-gray-500">
                    Si no vas a trabajar ese día, avisales a tus pacientes y
                    reprogramá antes de bloquear.
                  </span>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {n.state === 1 ? (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-600/90"
                  onClick={() => bloquear(n)}
                  disabled={checking !== null}
                >
                  {checking === n.date
                    ? "Revisando tu agenda…"
                    : "Bloquear mi agenda"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-600/90"
                  onClick={() =>
                    navigate(`${calendarPath("day")}?date=${n.date}`)
                  }
                >
                  Ver ese día en la Agenda
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700"
                onClick={() => {
                  // Si había abierto el panel de bloqueo y decidió trabajar, se cierra solo.
                  window.dispatchEvent(new CustomEvent("psi-block-close"));
                  activityTracker.trackAction("feriado_voy_a_trabajar");
                  marcarTrabajo(n.date);
                }}
              >
                Voy a trabajar
              </Button>
            </div>
          </div>
        ))}
      </div>
      {dialog}
    </div>
  );
};

export default HolidayAgendaNotice;
